import { useEffect } from "react";
import { BOARD_DISPLAY_NAME } from "@/config/browserBoard";
import {
  type AuthorizationState,
  createAuthorizationMediator,
  defaultAuthorizationServices,
  isAuthorizationEvent,
} from "@/features/app-auth/authorizationMediator";
import { useAppAuthMetadata } from "@/features/app-auth/useAppAuthMetadata";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import type { EventNode, TypedEvent } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface AuthorizationPresentation {
  clientName: string | null;
  stage: AuthorizationState["stage"];
  message: string;
  node: EventNode<TypedEvent>;
}

export function useAuthorizationPresenter(
  requestId: string,
  services = defaultAuthorizationServices,
): AuthorizationPresentation {
  useDocumentTitle(`アプリの利用を許可 - ${BOARD_DISPLAY_NAME}`);
  useAppAuthMetadata();
  const mediator = useHierarchyResource(
    (_previous) => createAuthorizationMediator(requestId, services),
    [requestId, services],
  );
  const state = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) => {
    if (!isAuthorizationEvent(event)) return "unhandled";
    return mediator.dispatch(event).status === "handled"
      ? "handled"
      : "unhandled";
  });
  useEffect(() => {
    if (!mediator.isDisposed) node.dispatch({ type: "authorization.start" });
  }, [node, mediator]);
  return {
    clientName: state.authorization?.clientName ?? null,
    stage: state.stage,
    message: state.message,
    node,
  };
}
